import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {connect} from 'react-redux';
import FlipMove from 'react-flip-move';
import {actions, select} from './connectors';

import {removePreloadContainer} from '~/utils/dom';
import {getScrollingElement, scrollToTop} from '~/utils/scrolling';
import {metrics} from '~/vendor/metrics';
import {pushGtmEvent} from '~/utils/analytics';

// constants
import Animations from '~/constants/Animations';
// components
import CanvasContainer from '~/components/CanvasContainer';
import ComplementsWrapper from '~/components/ComplementsWrapper';
import ContentSwitcher_Lg from '~/components/ContentSwitcher_Lg';
import InkyHeader_Lg from '~/components/InkyHeader_Lg';
import ModalContainer from '~/components/ModalContainer';
import MainMenuSidebar from '~/components/MainMenuSidebar';
import NextTools from '~/components/NextTools';
import NotificationContainer from '~/components/NotificationContainer';
import ProductSummary from '~/components/ProductSummary';
import ProductTray from '~/components/ProductTray';
import TrackingPixel from '~/components/TrackingPixel';
import VerticalToolbar from '~/components/VerticalToolbar';
// routes
import ModalRoutesLg from '~/router/ModalRoutesLg';
import GlobalRoutes from '~/router/GlobalRoutes';
import withRouter, {routerPropTypes} from '~/router/withRouter';
import {getCheckoutSummaryLink} from '~/constants/CheckoutLink';

import '~/components/App/index.scss';
import './index.scss';

const propTypes = {
  allowsNamesAndNumbers: PropTypes.bool,
  anchorStyleHasSleeveView: PropTypes.bool,
  anchorStyleId: PropTypes.string,
  blankDesign: PropTypes.bool,
  cartItemCount: PropTypes.number,
  compositeIds: PropTypes.arrayOf(PropTypes.string),
  currentProduct: PropTypes.object,
  designDirty: PropTypes.bool,
  determineLocation: PropTypes.func,
  getSelectedIconId: PropTypes.string,
  hasMultipleSelectedIcons: PropTypes.bool,
  hasNamesAndNumbers: PropTypes.bool,
  hasSleeveDesign: PropTypes.bool,
  hooks: PropTypes.object,
  icons: PropTypes.array,
  isCurrentDesignInCart: PropTypes.bool,
  isFundraisingMode: PropTypes.bool,
  isInternalUser: PropTypes.bool,
  isLabExitWarningEligible: PropTypes.bool,
  isMultiItemEligible: PropTypes.bool,
  isUnquoteable: PropTypes.bool,
  meta: PropTypes.objectOf(PropTypes.func),
  orderOptionsPath: PropTypes.string,
  prompt: PropTypes.func,
  resetAfterSavePath: PropTypes.func,
  router: routerPropTypes,
  selectIcons: PropTypes.func,
  setAfterSavePath: PropTypes.func,
  setCombineDesigns: PropTypes.func,
  setOrderOptionsPath: PropTypes.func,
  setOrderType: PropTypes.func,
  simplePricingTestEnabled: PropTypes.bool,
  simplePricingTestValue: PropTypes.string,
  validateActiveProducts: PropTypes.func,
  welcomeActive: PropTypes.bool,
  wtoTestGroup: PropTypes.string
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export class App_Lg extends Component {
  static displayName = 'App_Lg';
  static propTypes = propTypes;

  state = {
    contentHeight: '100%',
    backdropDimEnabled: true,
    isDisabled: false,
    isMainMenuOpen: false,
    isProductTrayEditing: false,
    slideOver: /^#\/next/.test(window.location.hash),
    trayPosition: 0,
    scrollPosition: 0,
    hasScrolled: false
  };

  async componentDidMount() {
    const {isFundraisingMode} = this.props;
    const {fundraisingModeWelcome} = this.props.meta;
    if (isFundraisingMode) fundraisingModeWelcome();
    this.updateContentNodeHeight();

    window.addEventListener('hashchange', this.onHashChange);
    window.addEventListener('resize', this.updateContentNodeHeight);
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('beforeunload', this.handleOnBeforeUnload);
    this.props.meta.ndxBreakpoint('large');

    window.addEventListener('dragenter', this.preventDefault);
    window.addEventListener('dragleave', this.preventDefault);
    window.addEventListener('dragover', this.preventDefault);
    window.addEventListener('drop', this.preventDefault);

    removePreloadContainer();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.isDisabled && !this.state.isDisabled) {
      getScrollingElement().scrollTop = this.state.scrollPosition;
    }
    if (
      prevProps?.router?.location?.pathname !==
      this.props?.router?.location?.pathname
    ) {
      this.onHashChange();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.onHashChange);
    window.removeEventListener('resize', this.updateContentNodeHeight);
    window.removeEventListener('scroll', this.handleScroll);
    window.cancelAnimationFrame(this.raf);

    window.removeEventListener('dragenter', this.preventDefault);
    window.removeEventListener('dragleave', this.preventDefault);
    window.removeEventListener('dragover', this.preventDefault);
    window.removeEventListener('drop', this.preventDefault);
  }

  handleScroll = () => {
    const scrollPosition =
      document.scrollTop || document.documentElement.scrollTop;
    scrollPosition > 20 && this.triggerScrollRender();
  };

  triggerScrollRender = () => {
    this.setState({hasScrolled: true});
    window.removeEventListener('scroll', this.handleScroll);
  };

  updateContentNodeHeight = () => {
    if (!this.running) {
      this.running = true;
      this.raf = window.requestAnimationFrame(() => {
        this.setState({contentHeight: this.getViewPortHeight()}, () => {
          this.running = false;
        });
      });
    }
  };

  updateHeaderHeight = ref => {
    this.headerHeight = 0;
    if (ref) {
      this.headerHeight = ref._height;
    }
  };

  getViewPortHeight() {
    const contentNodeHeight = window.innerHeight;
    return contentNodeHeight - this.headerHeight;
  }

  // Setting returnValue will simply show browser default text in dialog.
  // Only Chrome supports customizing the text (for now).
  handleOnBeforeUnload = event => {
    if (this.props.designDirty && this.props.isLabExitWarningEligible) {
      event.returnValue = 'Changes you made may not be saved.';
    }
  };

  onHashChange = () =>
    this.setState({
      slideOver: /^#\/next/.test(window.location.hash)
    });

  scrollUp(done) {
    scrollToTop(document.body, done);
  }

  handlers = {
    clickAddItems: () => {
      this.scrollUp(() => this.props.router.navigate('/addItems'));
    },
    clickAdditionalPrintArea: area =>
      this.scrollUp(() =>
        this.props.router.navigate(`/additionalPrintArea/${area}`)
      ),
    clickPickColor: (styleId, colorId) =>
      this.props.router.navigate(
        `/addItems/color${styleId ? `/${styleId}/${colorId}` : ''}`
      ),
    clickArt: () => this.props.router.navigate('/addArt?rs=m'),
    clickCloseAddItems: () => {
      this.props.router.navigate('/');
      this.props.meta.addItemsClose();
    },
    clickHelp: () => this.props.router.navigate('/help'),
    clickNamesAndNumbers: () => {
      const path = this.props.hasNamesAndNumbers
        ? '/namesAndNumbers/tools'
        : '/namesAndNumbers';
      this.props.router.navigate(path);
    },
    clickNotes: () => this.props.router.navigate('/notes'),
    clickPrintMethod: () => {
      const {router} = this.props;
      this.scrollUp(() => router.navigate('/printMethod'));
    },
    clickProductSummary: () => {
      this.scrollUp(() => this.props.router.navigate('/changeProduct'));
      this.props.meta.productSummaryTag();
    },
    clickStartFundraiser: () => this.props.router.navigate('/next/fundraising'),
    clickStartFundraiserGoals: () =>
      this.props.router.navigate('/next/fundraisingGoals'),
    clickShare: () => {
      metrics.fireEvent('lab ways to order', 'save');
      if (this.props.validateActiveProducts()) {
        this.props.router.navigate('/next/saveForm');
      }
    },
    clickText: () => {
      this.props.router.navigate('/addText');
      this.props.meta.textStart({
        source: 'menu'
      });
    },
    clickUpload: () => {
      this.props.router.navigate('/uploadForm');
      this.props.meta.uploadXPStart({
        source: 'menu'
      });
    },
    clickOverlay: e => {
      if (e.target.classList.contains('ndx-ModalSwitch-container')) {
        this.props.router.navigate('/');
        return;
      }

      const clickedAppDiv =
        e.target.classList && e.target.classList.contains('ndx-Content-app');

      if (this.state.isProductTrayEditing && clickedAppDiv) {
        this.setState({
          isProductTrayEditing: false
        });

        this.props.router.navigate('/');
      }
    }
  };

  clickProductColor = source => {
    const {currentProduct, router} = this.props;
    this.scrollUp(() => router.navigate('/productColor'));
    if (currentProduct) {
      metrics.fireEvent('change product color', source, currentProduct.name);
    }
    metrics.pageViewTag('ndx/productcolor');
    this.props.meta.changeProductColorStart({source});
  };

  clickProductColorFromTray = () => {
    this.clickProductColor('bottom bar');
  };

  clickProductColorFromSidebar = () => {
    this.clickProductColor('side bar');
  };

  handleMainMenuClick = () => {
    if (this.mainMenu) {
      this.mainMenu.toggleMenu();
    }
    this.props.meta.mainMenuOpen();
  };

  handleMainMenuToggle = isOpen => {
    this.setState({isMainMenuOpen: isOpen});
    this.toggleDisabled(isOpen);
  };

  // called when there are no items in the cart
  handleNext = async () => {
    const {
      validateActiveProducts,
      router,
      simplePricingTestEnabled,
      isUnquoteable,
      wtoTestGroup
    } = this.props;

    this.props.meta.quoteFormStart({quotePath: 'quote'});

    pushGtmEvent({
      event: 'ab_test_group',
      ab_test_name: '2024 12 12 simple pricing',
      ab_test_location: 'lab',
      ab_test_group: this.props.simplePricingTestValue
    });

    pushGtmEvent({
      event: 'ab_test_group',
      ab_test_name: '2025 04 28 optimize wto',
      ab_test_location: 'lab',
      ab_test_group: wtoTestGroup
    });

    if (!validateActiveProducts()) {
      return;
    }

    if (simplePricingTestEnabled && isUnquoteable) {
      router.navigate('/next/unquoteable');
    } else {
      router.navigate(this.props.orderOptionsPath);
    }
  };

  // called when there are already items in the cart
  handleAddToCart = () => {
    metrics.fireEvent('multi item cart', 'add to cart');
    const {
      router,
      validateActiveProducts,
      setCombineDesigns,
      simplePricingTestEnabled,
      isUnquoteable
    } = this.props;

    pushGtmEvent({
      event: 'ab_test_group',
      ab_test_name: '2024 12 12 simple pricing',
      ab_test_location: 'lab',
      ab_test_group: this.props.simplePricingTestValue
    });

    setCombineDesigns();

    if (!validateActiveProducts()) {
      return;
    }

    if (!simplePricingTestEnabled) {
      router.navigate('/next/quote');
      return;
    }

    const nextPath = isUnquoteable ? '/next/unquoteable' : '/next';
    router.navigate(nextPath);
  };

  handleAfterOffClick = () => this.props.router.navigate('/');

  // Pigment Modals come with their own backdrop dimming
  handleModalShow = noBackdropDim => {
    if (noBackdropDim) {
      this.setState({backdropDimEnabled: false});
    }
    this.toggleDisabled(true);
  };
  handleModalHide = noBackdropDim => {
    if (noBackdropDim) {
      this.setState({backdropDimEnabled: true});
    }
    this.toggleDisabled(false);
  };

  preventDefault = e => e.preventDefault();

  nextMenuClose = () => {
    this.props.router.navigate('/');
    this.props.resetAfterSavePath();
  };

  toggleProductTray = (isEditMode, path) => {
    const nextState = {
      isProductTrayEditing: isEditMode && !!path
    };
    if (isEditMode) {
      nextState.scrollPosition = getScrollingElement().scrollTop;
    }
    this.setState(nextState);
  };

  toggleDisabled(isDisabled) {
    this.setState({isDisabled});
  }

  animations() {
    const enterAnimation = {
      from: {
        transform: `translateX(${this.translateValue(100)}%)`,
        top: 0,
        bottom: 0
      },
      to: {
        transform: ''
      }
    };
    const leaveAnimation = {
      from: {
        transform: '',
        top: 0,
        bottom: 0
      },
      to: {
        transform: `translateX(${this.translateValue(-100)}%)`
      }
    };
    return {
      enterAnimation,
      leaveAnimation
    };
  }

  translateValue(value) {
    return this.state.slideOver ? value : value * -1;
  }

  isEditing() {
    return this.state.isProductTrayEditing;
  }

  navigateToHomepage = () => {
    window.location.href = window.location.origin;
  };

  navigateToCrop = () => {
    metrics.fireEvent('edit upload', 'crop button', 'click');
    this.props.router.navigate('/uploadTools/crop');
  };

  render() {
    const {
      allowsNamesAndNumbers,
      cartItemCount,
      hasMultipleSelectedIcons,
      isFundraisingMode,
      welcomeActive
    } = this.props;
    const {
      backdropDimEnabled,
      isDisabled,
      hasScrolled,
      isProductTrayEditing,
      slideOver
    } = this.state;

    const scrollPosition = Number(this.state.scrollPosition);
    const appClasses = classnames(
      'ndx-Content',
      'ndx-Content-app',
      {'ndx-Content-app--disabled': backdropDimEnabled && isDisabled},
      {'ndx-Content-app--preScroll': !hasScrolled && !slideOver},
      {'ndx-Content-app--tray-editing': isProductTrayEditing}
    );
    const styleRules = {
      height: `${window.innerHeight + scrollPosition}px`,
      overflow: 'hidden',
      position: 'absolute',
      top: -scrollPosition
    };
    const style = isDisabled || isProductTrayEditing ? styleRules : null;
    const shouldHideUnfocusedComponents = Boolean(isDisabled || slideOver);

    return (
      <MainMenuSidebar
        ref={ref => (this.mainMenu = ref)}
        onItemClick={this.props.meta.mainMenuNavigate}
        onMenuToggle={this.handleMainMenuToggle}>
        <div
          className={appClasses}
          style={style}
          onClick={this.handlers.clickOverlay}>
          <InkyHeader_Lg
            ref={this.updateHeaderHeight}
            cartItemCount={cartItemCount}
            designDirty={this.props.designDirty}
            internal={this.props.isInternalUser}
            isFundraisingMode={isFundraisingMode}
            menuActive={this.state.isMainMenuOpen}
            prompt={this.props.prompt}
            welcome={welcomeActive}
            onChatClick={this.props.meta.chat}
            onLogoClick={this.navigateToHomepage}
            onMainMenuClick={this.handleMainMenuClick}
          />
          <div className="ndx-App ndx-App-container">
            <div
              className="ndx-Container ndx-Container--relative ndx-App"
              style={{height: this.state.contentHeight}}>
              <FlipMove {...this.animations()} className="ndx-Content-main">
                {!this.state.slideOver && (
                  <div
                    key="menu"
                    className="ndx-App-menuContainer ndx-Content-menu--floating u-clickPassThrough">
                    <VerticalToolbar
                      key="menu"
                      {...this.handlers}
                      clickProductColor={this.clickProductColorFromSidebar}
                      isFundraisingMode={isFundraisingMode}
                      allowsNamesAndNumbers={allowsNamesAndNumbers}
                    />
                    <ContentSwitcher_Lg
                      key="content"
                      hasMultipleSelectedIcons={hasMultipleSelectedIcons}
                      isFundraisingMode={isFundraisingMode}
                      slideOver={this.state.slideOver}
                    />
                  </div>
                )}
                <div key="canvas" className="ndx-App-canvasContainer">
                  <CanvasContainer
                    handleAfterOffClick={this.handleAfterOffClick}
                    isFundraisingMode={isFundraisingMode}
                    isLarge
                    showTools={!this.state.slideOver}
                    onClick={this.state.slideOver ? this.nextMenuClose : null}
                    navigateToCrop={this.navigateToCrop}
                  />
                </div>
                {this.state.slideOver && (
                  <NextTools
                    key="next"
                    animationIn={Animations.slideLeftIn}
                    animationOut={Animations.slideLeftOut}
                    blankDesign={this.props.blankDesign}
                    className={classnames(
                      'ndx-Content-menu--floating',
                      'ndx-Content-menu--next',
                      'ndx-Content-menu--full'
                    )}
                    designDirty={this.props.designDirty}
                    hooks={this.props.hooks}
                    setAfterSavePath={this.props.setAfterSavePath}
                    setOrderOptionsPath={this.props.setOrderOptionsPath}
                    onClose={this.nextMenuClose}
                  />
                )}
              </FlipMove>
            </div>
            {this.state.hasScrolled && (
              <ProductSummary
                handleAdditionalPrintAreasClick={
                  this.handlers.clickAdditionalPrintArea
                }
                isDisabled={shouldHideUnfocusedComponents}
                isFundraisingMode={isFundraisingMode}
                largeBreakPoint
              />
            )}
            <div
              className="ndx-complementsSection"
              style={{display: this.state.slideOver ? 'none' : 'block'}}>
              <div className="ndx-complementsSection-column">
                <ComplementsWrapper
                  checkScroll
                  inSkirt
                  isWide={false}
                  isDisabled={shouldHideUnfocusedComponents}
                  isFundraisingMode={isFundraisingMode}
                  onClickOrderMore={this.props.meta.orderMoreAddStyles}
                  onPickProduct={(styleId, colorId) =>
                    this.scrollUp(() =>
                      this.handlers.clickPickColor(styleId, colorId)
                    )
                  }
                />
              </div>
            </div>
          </div>
          <ModalRoutesLg
            anchorStyleHasSleeveView={this.props.anchorStyleHasSleeveView}
            handleSwitcherStatusChange={this.toggleProductTray}
            handleAddItemBrowseCatalog={this.handlers.clickProductSummary}
            handleAddItemOnClose={this.handlers.clickCloseAddItems}
            handleAddItemPickProduct={this.handlers.clickPickColor}
            isFundraisingMode={isFundraisingMode}
            meta={this.props.meta}
          />
          <GlobalRoutes />
          <ProductTray
            isDisabled={shouldHideUnfocusedComponents}
            isEditing={this.isEditing()}
            isFundraisingMode={isFundraisingMode}
            isSlideOver={this.state.slideOver}
            onAddItemsClick={this.handlers.clickAddItems}
            onChangeProductClick={this.handlers.clickProductSummary}
            onFundraisingClick={this.handlers.clickStartFundraiserGoals}
            onOrderClick={this.handleNext}
            onAddToCartClick={this.handleAddToCart}
            onProductColorClick={this.clickProductColorFromTray}
            onPrintMethodClick={this.handlers.clickPrintMethod}
            onShareClick={this.handlers.clickShare}
          />
          <NotificationContainer
            cartPrimaryAction={() => {
              window.location.href = getCheckoutSummaryLink();
            }}
            onHide={this.handleModalHide}
            onShow={this.handleModalShow}
          />
          <ModalContainer
            onHide={this.handleModalHide}
            onShow={this.handleModalShow}
          />
          {!this.props.isInternalUser && <TrackingPixel />}
        </div>
      </MainMenuSidebar>
    );
  }
}

export default connect(
  () => select(),
  dispatch => actions(dispatch)
)(withRouter(App_Lg));
